import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import { UploadFile } from 'antd/es/upload';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { useEffect, useState } from 'react';
import Modal from 'antd/lib/modal/Modal';
import { CustomMessage } from '../../../hooks';
import GraphqlService from '../../../services/graphql/GraphqlService';
import { Query } from '../../../services/graphql/query';
import { FormInstance } from 'antd/es/form';
import { useDniBackExtraction } from '../../../pages/AddPerson/hooks/useDniBackExtraction';
import { IDetectedTextForFormField } from '../../../interfaces/vision';
import { INationality } from '../../../interfaces/nationality';

interface IDataExtractionProps {
  form: FormInstance;
  title: string;
  type: 'dniFront' | 'dniBack' | 'transferForm';
  fileList: UploadChangeParam<UploadFile<any>> | undefined;
  setFileList: React.Dispatch<
    React.SetStateAction<UploadChangeParam<UploadFile<any>> | undefined>
  >;
  onRemove: () => void;
  nationalities?: INationality[];
}

const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const DataExtraction = (props: IDataExtractionProps) => {
  const { form, title, type, fileList, setFileList, nationalities, onRemove } =
    props;
  const [data, setData] = useState<string>('');
  const [dataPdf, setDataPdf] = useState<IDetectedTextForFormField>();
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const { customFileRequest } = GraphqlService();
  const { messageError } = CustomMessage();
  const {
    dni,
    placeOfBirth,
    birthDate,
    gender,
    cuil,
    nationality,
    firstName,
    lastName,
    home,
  } = useDniBackExtraction();

  const handlePreview = async (file: UploadFile) => {
    if (type !== 'transferForm') {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as RcFile);
      }
      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
      setPreviewTitle(
        file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
      );
    }
  };

  const uploadButton = (
    <div>
      {type !== 'transferForm' ? (
        <>
          <PlusOutlined />

          <div style={{ marginTop: 8 }}>Cargar Imagen</div>
        </>
      ) : (
        <Button icon={<UploadOutlined />}>Cargar Pdf</Button>
      )}
    </div>
  );

  /**
   * Valida si un valor de nombre es válido (no es número, DNI, etc.)
   */
  const isValidNameValue = (value: string | undefined): boolean => {
    if (!value || value.trim().length < 2) return false;
    
    const trimmed = value.trim();
    
    // Rechazar si es solo números
    if (/^\d+$/.test(trimmed)) return false;
    
    // Rechazar formato de DNI argentino (XX.XXX.XXX o XXXXXXXX)
    if (/^\d{1,2}\.\d{3}\.\d{3}$/.test(trimmed)) return false;
    if (/^\d{8,9}$/.test(trimmed)) return false;
    
    // Rechazar si tiene demasiados números (>50% son dígitos)
    const digitCount = (trimmed.match(/\d/g) || []).length;
    if (digitCount > 0 && digitCount / trimmed.length > 0.5) return false;
    
    return true;
  };

  const fillPersonForm = (value: string) => {
    console.log("=== INICIO EXTRACCIÓN DE DATOS DNI ===");
    console.log("response of detectText");
    console.log(value);
    console.log("=====================================");
    const detections = value.split('\n');

    // Extraer todos los campos
    const extractedDni = dni(detections);
    const extractedCuil = cuil(detections);
    const extractedFirstName = firstName(detections);
    const extractedLastName = lastName(detections);
    const extractedPlaceOfBirth = placeOfBirth(detections);
    const extractedHome = home(detections);
    const extractedGender = gender(detections);
    const extractedBirthDate = birthDate(detections);
    const IsoNationality = nationality(detections);

    const nationalityFound: INationality | undefined = nationalities?.find(
      (nationality) => nationality.iso === IsoNationality,
    );

    
    // Logging detallado en desarrollo
    if (process.env.NODE_ENV === 'development') {
      console.log("=== CAMPOS EXTRAÍDOS ===");
      console.log("DNI:", extractedDni);
      console.log("CUIL:", extractedCuil);
      console.log("Nombre:", extractedFirstName);
      console.log("Apellido:", extractedLastName);
      console.log("Lugar de Nacimiento:", extractedPlaceOfBirth);
      console.log("Domicilio:", extractedHome);
      console.log("Género ID:", extractedGender);
      console.log("Fecha de Nacimiento:", extractedBirthDate);
      console.log("Nacionalidad ISO:", IsoNationality);
      console.log("Nacionalidad ID:", nationalityFound?.id);
      console.log("========================");
    }
    const formValues = form.getFieldsValue();

    form.setFieldsValue({
      place_of_birth: formValues.place_of_birth
        ? formValues.place_of_birth
        : extractedPlaceOfBirth,
      document_number: formValues.document_number
        ? formValues.document_number
        : extractedDni,
      cuil_cuit: formValues.cuil_cuit ? formValues.cuil_cuit : extractedCuil,
      gender_id: formValues.gender_id
        ? formValues.gender_id
        : extractedGender,
      date_of_birth: formValues.date_of_birth
        ? formValues.date_of_birth
        : extractedBirthDate,
      nationality_id: formValues.nationality_id
        ? formValues.nationality_id
        : nationalityFound?.id,
      firstname: formValues.firstname && isValidNameValue(formValues.firstname)
        ? formValues.firstname
        : extractedFirstName,
      lastname:
        formValues.lastname &&
        formValues.lastname.toUpperCase().trim() !== 'EXTRANJERO' &&
        isValidNameValue(formValues.lastname)
          ? formValues.lastname
          : extractedLastName,
      home: formValues.home ? formValues.home : extractedHome,
    });

  };

  const fillTransferForm = (value: IDetectedTextForFormField) => {
    form.setFieldsValue({
      plan_number: value.plan_number,
      provincial_part_number: value.provincial_part_number,
      municipal_part_number: value.municipal_part_number,
      unit_type_id: value.unit_type_id,
      unit_number: value.unit_number,
      header2: value.header2,
      header6: value.header6,
      header7: value.header7,
      header8: value.header8,
      observations: value.observations,
    });
  };

  useEffect(() => {
    if (data && (type === 'dniBack' || type === 'dniFront')) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DataExtraction] ${type} raw OCR lines:`, data.split('\n'));
      }
      fillPersonForm(data);
    }
  }, [data]);

  useEffect(() => {
    if (dataPdf && type === 'transferForm') {
      fillTransferForm(dataPdf);
    }
  }, [dataPdf]);

  const detectText = async (file: any) => {
    try {
      const res = await customFileRequest(
        {
          mutation:
            type === 'transferForm'
              ? Query.detectTextOnPdfForFormField
              : Query.detectText,
          variables: {
            file: file
              ? {
                  filename: file?.filename,
                  mimetype: file?.type,
                  encoding: 'base64',
                }
              : undefined,
          },
        },
        file
          ? [
              {
                file: file,
                path: 'variables.file',
              },
            ]
          : [],
      );

      return res;
    } catch (error: any) {
      if (error.status_code && error.message) {
        return messageError({
          context: 'ImageDataExtraction.detectText.1',
          message: error.message,
        });
      }
    }
  };

  return (
    <>
      <h4>{title}</h4>
      {type === 'transferForm' ? (
        <p>Formato permitido: PDF</p>
      ) : (
        <p>Formato permitido: JPG / JPEG / PNG</p>
      )}
      <p>El tamaño máximo permitido del archivo es 4 MB.</p>
      <Upload
        listType={type === 'transferForm' ? 'picture' : 'picture-card'}
        onPreview={handlePreview}
        className={`upload-dni`}
        onChange={(info) => {
          setFileList(info);
        }}
        beforeUpload={(file) => {
          let isPNG = true;
          let isJPG = true;
          let isPDF = true;

          if (type !== 'transferForm') {
            isPNG = file.type === 'image/png';
            isJPG = file.type === 'image/jpeg';
            if (!isPNG && !isJPG) {
              messageError({
                context: 'ImageDataExtraction.beforeUpload.1',
                message: 'Solo se permiten archivos PNG o JPG o JPEG',
              });
              return false;
            }
          }

          if (type === 'transferForm') {
            isPDF = file.type === 'application/pdf';
            if (!isPDF) {
              messageError({
                context: 'ImageDataExtraction.beforeUpload.1',
                message: 'Solo se permiten archivos PDF',
              });
              return false;
            }
          }

          const goodFileSize = file.size < 4194304;

          if (!goodFileSize) {
            messageError({
              context: 'ImageDataExtraction.beforeUpload.2',
              message: 'El tamaño máximo permitido del archivo es 4 MB.',
            });
            return false;
          }

          return isPNG || isJPG || isPDF || goodFileSize;
        }}
        fileList={fileList?.fileList}
        onRemove={() => {
          onRemove();
          setData('');
        }}
        customRequest={async (uploadRequestOptions) => {
          const { onSuccess, file } = uploadRequestOptions;
          const fileRc = file as RcFile;
          if (onSuccess) {
            const res = await detectText(fileRc);
            if (res && type === 'transferForm') {
              setDataPdf(res);
            }
            if (res && (type === 'dniBack' || type === 'dniFront')) {
              setData(res.response);
            }
            onSuccess('');
          }
        }}
      >
        {fileList?.fileList.length ? undefined : uploadButton}
      </Upload>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={1000}
      >
        <img alt="dni" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </>
  );
};
